import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { startOfMonth, endOfMonth, startOfDay, isBefore, addMonths } from 'date-fns';
import { Shift, DayShift, BusinessClosedDate, DateClosureStatus } from '@/types';
import { Tables } from '@/integrations/supabase/types';
import { TimeLogQueryResult } from '@/types/shift-components';
import { ShiftTemplate } from '../ShiftTemplateItem';
import { useEmployees } from '@/hooks/employees';
import { useBusinesses } from '@/hooks/business/useBusinesses';
import { useSimpleDisplayHours, type SimpleDisplayHoursRange } from '@/hooks/scheduling/useSimpleDisplayHours';
import { usePeriodStatuses } from '@/hooks/analytics/usePeriodStatuses';
import { useDayTemplates } from '@/hooks/scheduling/useDayTemplates';
import { useBusinessTime } from '@/hooks/ui/useBusinessTime';
import { UnifiedShiftService } from '@/lib/scheduling/unified-shift-service';
import { useShiftStatusService } from '@/hooks/scheduling/useShiftStatusService';
import { employeeAvailabilityCache } from '@/lib/employees/EmployeeAvailabilityCache';
import { useMonthShiftsQuery, useInvalidateShifts } from '@/hooks/scheduling/useShiftsQuery';
import { useAssignEmployee, useBatchAssignEmployee } from '@/hooks/scheduling/useShiftMutations';
import { useHistoricalEdit } from '@/context/HistoricalEditContext';
import { useBusinessClosedDates } from '@/hooks/business/useBusinessClosedDates';

// Types
type Event = Tables<'events'>;
type EventAssignment = Tables<'event_assignments'>;

interface EventWithAssignments extends Event {
  event_assignments: (EventAssignment & {
    employees: {
      name: string;
      email: string;
    };
  })[];
}

// Data Context - handles all data fetching and business logic
interface ShiftDataContextValue {
  // Core data
  shifts: DayShift[];
  employees: any[];
  timeLogs: TimeLogQueryResult[];
  shiftTemplates: ShiftTemplate[];
  dayTemplates: any[];
  events: EventWithAssignments[];
  periodStatuses: any;
  selectedBusinessDetails: any;
  periodEmployeeStatuses: any[];

  // Display hours
  currentMonthDisplayHours: {
    day_of_week: number;
    is_open: boolean;
    open_time?: string;
    close_time?: string;
    is_24_hours?: boolean;
  }[] | null;
  displayStartTime: Date | null;
  displayEndTime: Date | null;

  // Simple display hours for shift management
  simpleDisplayHours: {
    range: SimpleDisplayHoursRange;
    calculationMethod: 'user-override' | 'opening-hours' | 'fallback';
    canExpand: boolean;
    expandRange: (newRange: Partial<SimpleDisplayHoursRange>) => void;
    resetToAuto: () => void;
    isLoading: boolean;
  };

  // Loading states
  loadingShifts: boolean;
  loadingEmployees: boolean;
  loadingEvents: boolean;
  loadingShiftTemplates: boolean;
  loadingDayTemplates: boolean;
  loadingDisplayHours: boolean;
  periodStatusesLoading: boolean;

  // Error states
  periodStatusesError: string | null;

  // Business data
  businessId: string | null;
  businessTimezone: string;
  countryCode: string | null;
  allShiftsAssigned: boolean;

  // Performance stats
  performanceStats: any;

  // Data actions
  refetchShifts: () => void;
  refetchPeriodStatuses: (() => void) | null;
  handleShiftCreated: () => void;
  handleShiftUpdated: () => void;
  handleShiftDeleted: () => void;
  handleAssignEmployeeToShift: (shiftId: string, employeeId: string | null) => Promise<void>;
  handleBatchAssignEmployee: (shiftIds: string[], employeeId: string | null) => Promise<void>;
  fetchShiftTemplates: () => Promise<void>;

  // Template actions
  handleEditDayTemplate: (id: string) => void;
  handleDeleteDayTemplate: (id: string) => void;

  // Day template edit dialog state
  isEditDialogOpen: boolean;
  editedTemplate: any | null;
  editedName: string;
  editedDescription: string;
  editedShifts: any[];
  setEditedName: (name: string) => void;
  setEditedDescription: (description: string) => void;
  handleSaveTemplate: () => Promise<void>;
  closeEditDialog: () => void;

  // Closed dates - for marking individual days as closed
  closedDatesMap: Map<string, BusinessClosedDate>;
  closedDatesLoading: boolean;
  addClosedDate: (date: Date, reason?: string) => Promise<BusinessClosedDate>;
  removeClosedDate: (id: string) => Promise<void>;
  isDateClosed: (date: Date) => DateClosureStatus;
  checkExistingShifts: (date: Date) => Promise<{ count: number; hasAssignedShifts: boolean }>;
  refreshClosedDates: () => Promise<void>;
}

const ShiftDataContext = createContext<ShiftDataContextValue | undefined>(undefined);

interface ShiftDataProviderProps {
  children: ReactNode;
  businessId: string | null;
  currentDisplayMonth: Date; // From UI provider
}

export const ShiftDataProvider: React.FC<ShiftDataProviderProps> = ({
  children,
  businessId,
  currentDisplayMonth
}) => {
  // Business data
  const { businessTimezone } = useBusinessTime();
  const { businesses } = useBusinesses();

  // Timezone-aware shift status service
  const shiftStatusService = useShiftStatusService();

  // Historical edit context for tracking edits to past shifts
  const { recordHistoricalEdit, isHistoricalEditEnabled } = useHistoricalEdit();

  // Derived business data
  const selectedBusinessDetails = businesses?.find(b => b.id === businessId) || null;
  const countryCode = selectedBusinessDetails?.country_code || null;
  const openingHours = selectedBusinessDetails?.opening_hours || [];

  // Calculate closed dates range - cover current month + next month for period statuses
  const closedDatesRange = useMemo(() => {
    const today = new Date();
    return {
      start: startOfMonth(today),
      end: endOfMonth(addMonths(today, 1))
    };
  }, []);

  // Closed dates for marking individual days as closed
  const {
    closedDatesMap,
    loading: closedDatesLoading,
    addDate: addClosedDate,
    removeDate: removeClosedDate,
    isDateClosed,
    checkExistingShifts,
    refresh: refreshClosedDates
  } = useBusinessClosedDates({
    businessId: businessId || null,
    startDate: closedDatesRange.start,
    endDate: closedDatesRange.end,
    openingHours
  });

  // Core data hooks
  const {
    employees,
    loading: loadingEmployees
  } = useEmployees({
    businessId: businessId,
    dataLevel: 'standard'
  });

  // Calculate date range for shift fetching
  const dateRange = useMemo(() => {
    const start = startOfMonth(currentDisplayMonth);
    const end = endOfMonth(currentDisplayMonth);
    return { start, end };
  }, [currentDisplayMonth]);

  // Use React Query for shift data - provides caching, background refetch, optimistic updates
  const {
    shifts: queryShifts,
    isLoading: queryLoading,
    isFetching: queryFetching,
    refetch: queryRefetch,
    performanceStats: queryPerformanceStats,
  } = useMonthShiftsQuery({
    businessId,
    month: currentDisplayMonth,
    employees,
    hidePastDays: false,
    enabled: !!businessId,
  });

  // Get invalidation helper for cache management
  const { invalidateAll } = useInvalidateShifts();

  // Local state for shifts - allows optimistic updates while maintaining backward compatibility
  const [localShifts, setLocalShifts] = useState<DayShift[]>([]);
  const [shiftPerformanceStats, setShiftPerformanceStats] = useState<any>({});

  // Sync query data to local state when it changes
  useEffect(() => {
    if (queryShifts) {
      setLocalShifts(queryShifts);
    }
    if (queryPerformanceStats) {
      setShiftPerformanceStats(queryPerformanceStats);
    }
  }, [queryShifts, queryPerformanceStats]);

  // Use local shifts for optimistic updates, fallback to query shifts
  const shifts = localShifts.length > 0 ? localShifts : (queryShifts || []);

  // Loading state: only show loading on initial load when no data exists
  // This keeps the calendar visible during background refetches
  const loadingShifts = queryLoading && shifts.length === 0;

  // Period statuses for analytics
  const {
    periodStatuses,
    loading: periodStatusesLoading,
    error: periodStatusesError,
    refetch: refetchPeriodStatuses
  } = usePeriodStatuses(businessId);

  // Mock data (to be replaced with actual hooks later)
  const [timeLogs] = useState<TimeLogQueryResult[]>([]);
  const [periodEmployeeStatuses] = useState<any[]>([]);

  const {
    dayTemplates,
    loadingTemplates: loadingDayTemplates,
    handleEditTemplate: handleEditDayTemplate,
    handleDeleteTemplate: handleDeleteDayTemplate,
    isEditDialogOpen,
    editedTemplate,
    editedName,
    editedDescription,
    editedShifts,
    setEditedName,
    setEditedDescription,
    handleSaveTemplate,
    closeEditDialog
  } = useDayTemplates({ businessId });

  // Prepare shifts for display hours calculation (format Date objects to HH:mm strings)
  const shiftsForDisplayHours = useMemo(() => {
    if (!shifts || shifts.length === 0) return [];
    return shifts.map(shift => ({
      startTime: `${shift.startTime.getHours().toString().padStart(2, '0')}:${shift.startTime.getMinutes().toString().padStart(2, '0')}`,
      endTime: `${shift.endTime.getHours().toString().padStart(2, '0')}:${shift.endTime.getMinutes().toString().padStart(2, '0')}`
    }));
  }, [shifts]);

  // Simple display hours for shift management (single source of truth)
  // Now unified: takes most expansive range from shifts AND opening hours
  // Uses 'multi-day' mode to find widest range across all days (for month/week calendar views)
  const simpleDisplayHours = useSimpleDisplayHours({
    businessId: businessId || '',
    date: currentDisplayMonth.toISOString().split('T')[0],
    openingHours: selectedBusinessDetails?.opening_hours || [],
    shifts: shiftsForDisplayHours,
    mode: 'multi-day'
  });

  // Derive loading state from simple display hours
  const loadingDisplayHours = simpleDisplayHours.isLoading;

  // State for templates and events
  const [shiftTemplates, setShiftTemplates] = useState<ShiftTemplate[]>([]);
  const [events, setEvents] = useState<EventWithAssignments[]>([]);
  const [loadingShiftTemplates, setLoadingShiftTemplates] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);

  // Derive display times from simpleDisplayHours (single source of truth)
  // currentMonthDisplayHours is deprecated - not used anywhere
  const currentMonthDisplayHours = null;

  // Calculate display times from simpleDisplayHours range
  const displayStartTime = useMemo(() => {
    const { startTime } = simpleDisplayHours.range;
    return new Date(`1970-01-01T${startTime}:00`);
  }, [simpleDisplayHours.range.startTime]);

  const displayEndTime = useMemo(() => {
    const { endTime, end } = simpleDisplayHours.range;
    // Handle overnight hours (end > 24) by capping the Date at 23:59
    // The actual hour value is preserved in simpleDisplayHours.range.end
    if (end >= 24) {
      return new Date(`1970-01-01T23:59:00`);
    }
    return new Date(`1970-01-01T${endTime}:00`);
  }, [simpleDisplayHours.range.endTime, simpleDisplayHours.range.end]);

  // Calculate if all shifts are assigned
  const allShiftsAssigned = shifts?.every(shift => shift.employeeId) || false;

  // Performance stats from shift fetching
  const performanceStats = {
    ...shiftPerformanceStats,
    totalShifts: shifts?.length || 0,
    assignedShifts: shifts?.filter(s => s.employeeId).length || 0,
    totalEmployees: employees?.length || 0
  };

  // Use React Query mutations for optimistic updates
  const assignEmployeeMutation = useAssignEmployee();
  const batchAssignMutation = useBatchAssignEmployee();

  // Data manipulation actions
  const refetchShifts = useCallback(() => {
    // Invalidate React Query cache to trigger background refetch
    if (businessId) {
      invalidateAll(businessId);
    }
    refetchPeriodStatuses?.();
  }, [businessId, invalidateAll, refetchPeriodStatuses]);

  const handleShiftCreated = useCallback(() => {
    // React Query will handle the refetch via cache invalidation
    refetchShifts();
    // Invalidate all availability cache since new shift affects availability for all employees
    employeeAvailabilityCache.invalidateCache('shift-created');
  }, [refetchShifts]);

  const handleShiftUpdated = useCallback(() => {
    // React Query will handle the refetch via cache invalidation
    refetchShifts();
    // Invalidate all availability cache since shift times may have changed
    employeeAvailabilityCache.invalidateCache('shift-updated');
  }, [refetchShifts]);

  const handleShiftDeleted = useCallback(() => {
    // React Query will handle the refetch via cache invalidation
    refetchShifts();
    // Invalidate all availability cache since deleted shift frees up employee availability
    employeeAvailabilityCache.invalidateCache('shift-deleted');
  }, [refetchShifts]);

  const handleAssignEmployeeToShift = useCallback(async (shiftId: string, employeeId: string | null) => {
    try {
      // Find the shift to get its details for historical edit tracking
      const targetShift = shifts.find(s => s.id === shiftId);
      const oldEmployeeId = targetShift?.employeeId;

      // Use React Query mutation with optimistic updates
      await assignEmployeeMutation.mutateAsync({ shiftId, employeeId });

      // Check if this is a historical edit (shift is in the past)
      if (targetShift && businessId) {
        const today = startOfDay(new Date());
        const shiftDay = startOfDay(targetShift.startTime);
        const isHistoricalShift = isBefore(shiftDay, today);

        // Record historical edit if editing a past shift
        if (isHistoricalShift && isHistoricalEditEnabled) {
          await recordHistoricalEdit(
            shiftId,
            businessId,
            'employee_changed',
            { employeeId: oldEmployeeId },
            { employeeId: employeeId }
          );
        }
      }

      // Also update local state for immediate UI feedback
      setLocalShifts(prevShifts =>
        prevShifts.map(shift => {
          if (shift.id === shiftId) {
            const newStatus = shiftStatusService.calculateShiftStatus(
              shift.startTime,
              shift.endTime,
              employeeId,
              shift.actualArrivalTime || undefined,
              shift.actualDepartureTime || undefined
            );

            return {
              ...shift,
              employeeId: employeeId,
              calculatedStatus: newStatus,
              // Mark as historically edited if it was a past shift
              wasHistoricallyEdited: shift.wasHistoricallyEdited || (isBefore(startOfDay(shift.startTime), startOfDay(new Date())) && isHistoricalEditEnabled),
            };
          }
          return shift;
        })
      );
    } catch (error) {
      console.error('Error assigning employee to shift:', error);
      throw error;
    }
  }, [assignEmployeeMutation, shifts, businessId, recordHistoricalEdit, isHistoricalEditEnabled]);

  const handleBatchAssignEmployee = useCallback(async (shiftIds: string[], employeeId: string | null) => {
    try {
      // Use React Query mutation with optimistic updates
      await batchAssignMutation.mutateAsync({ shiftIds, employeeId });

      // Record historical edits for past shifts
      if (businessId && isHistoricalEditEnabled) {
        const today = startOfDay(new Date());

        for (const shiftId of shiftIds) {
          const targetShift = shifts.find(s => s.id === shiftId);
          if (targetShift) {
            const shiftDay = startOfDay(targetShift.startTime);
            const isHistoricalShift = isBefore(shiftDay, today);

            if (isHistoricalShift) {
              await recordHistoricalEdit(
                shiftId,
                businessId,
                'employee_changed',
                { employeeId: targetShift.employeeId },
                { employeeId: employeeId }
              );
            }
          }
        }
      }

      // Also update local state for immediate UI feedback
      setLocalShifts(prevShifts =>
        prevShifts.map(shift => {
          if (shiftIds.includes(shift.id)) {
            const newStatus = shiftStatusService.calculateShiftStatus(
              shift.startTime,
              shift.endTime,
              employeeId,
              shift.actualArrivalTime || undefined,
              shift.actualDepartureTime || undefined
            );

            return {
              ...shift,
              employeeId: employeeId,
              calculatedStatus: newStatus,
              // Mark as historically edited if it was a past shift
              wasHistoricallyEdited: shift.wasHistoricallyEdited || (isBefore(startOfDay(shift.startTime), startOfDay(new Date())) && isHistoricalEditEnabled),
            };
          }
          return shift;
        })
      );
    } catch (error) {
      console.error('Error batch assigning employees:', error);
      throw error;
    }
  }, [batchAssignMutation, shifts, businessId, recordHistoricalEdit, isHistoricalEditEnabled]);

  const fetchShiftTemplates = useCallback(async () => {
    if (!businessId) return;

    setLoadingShiftTemplates(true);
    try {
      // Implementation would go here
      setShiftTemplates([]);
    } catch (error) {
      console.error('Error fetching shift templates:', error);
    } finally {
      setLoadingShiftTemplates(false);
    }
  }, [businessId]);

  // Load shift templates when businessId changes
  useEffect(() => {
    if (businessId) {
      fetchShiftTemplates();
    }
  }, [businessId, fetchShiftTemplates]);

  const value: ShiftDataContextValue = {
    // Core data
    shifts: shifts || [],
    employees: employees || [],
    timeLogs: timeLogs || [],
    shiftTemplates,
    dayTemplates: dayTemplates || [],
    events,
    periodStatuses,
    selectedBusinessDetails,
    periodEmployeeStatuses: periodEmployeeStatuses || [],

    // Display hours
    currentMonthDisplayHours,
    displayStartTime,
    displayEndTime,
    simpleDisplayHours,

    // Loading states
    loadingShifts,
    loadingEmployees,
    loadingEvents,
    loadingShiftTemplates,
    loadingDayTemplates,
    loadingDisplayHours,
    periodStatusesLoading,

    // Error states
    periodStatusesError,

    // Business data
    businessId,
    businessTimezone,
    countryCode,
    allShiftsAssigned,

    // Performance stats
    performanceStats,

    // Actions
    refetchShifts,
    refetchPeriodStatuses,
    handleShiftCreated,
    handleShiftUpdated,
    handleShiftDeleted,
    handleAssignEmployeeToShift,
    handleBatchAssignEmployee,
    fetchShiftTemplates,
    handleEditDayTemplate,
    handleDeleteDayTemplate,

    // Day template edit dialog state
    isEditDialogOpen,
    editedTemplate,
    editedName,
    editedDescription,
    editedShifts,
    setEditedName,
    setEditedDescription,
    handleSaveTemplate,
    closeEditDialog,

    // Closed dates
    closedDatesMap,
    closedDatesLoading,
    addClosedDate,
    removeClosedDate,
    isDateClosed,
    checkExistingShifts,
    refreshClosedDates,
  };

  return <ShiftDataContext.Provider value={value}>{children}</ShiftDataContext.Provider>;
};

export const useShiftData = (): ShiftDataContextValue => {
  const context = useContext(ShiftDataContext);
  if (context === undefined) {
    throw new Error('useShiftData must be used within a ShiftDataProvider');
  }
  return context;
};

/**
 * Safe version of useShiftData that returns null instead of throwing
 * when used outside of ShiftDataProvider (e.g., when in infinite scroll mode)
 */
export const useShiftDataSafe = (): ShiftDataContextValue | null => {
  const context = useContext(ShiftDataContext);
  return context ?? null;
};